import { useCallback, useEffect, useState } from 'react';
import type { Key } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Space,
  Select,
  Button,
  Table,
  Popconfirm,
  message,
  Transfer,
  Typography,
  Spin,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TransferDirection } from 'antd/es/transfer';
import type { Company, User } from '@viq-admin/types';
import {
  grantCompanyAccess,
  revokeCompanyAccess,
  listConferenceCompanies,
  listConferenceUsers,
  bulkAssign,
  bulkUnassign,
} from '../../api/conferences';
import type { UserWithAssignments } from '../../api/users';
import { superadminFetch } from '../../api/dataProvider';
import { useCompanyScope } from '../../components/CompanyScopePicker';
import { CompanyLink, UserLink } from '../../components/EntityLinks';
import { useResizableColumns, space } from '@viq-admin/ui';
import { Can } from '../../ability/AbilityContext';

/**
 * Assignments tab — the portal folds company grants AND rep assignment into one
 * screen, so the console mirrors that: a "Companies with access" card (grant /
 * revoke) followed by a per-granted-company rep Transfer. You grant a company,
 * then assign its reps — assignments only appear for companies that have access.
 *
 * Operate-as-tenant scope: when a tenant is selected in the top-right picker the
 * whole tab narrows to that tenant (its grant status + its rep Transfer). "All
 * tenants" shows every granted company (cross-tenant union). Writes carry
 * `targetCompanyId` and are gated on `manage Conference`.
 *
 * Company and rep names link to their pages. In the Transfer the rep name is
 * the link and the email beside it stays plain, so clicking the email (or the
 * checkbox) still selects the row; the link stops propagation, so following
 * it never toggles the selection.
 */

interface TransferItem {
  key: string;
  /** "Name (email)", or the bare email. The Transfer's search matches this. */
  title: string;
  /** Link label: the rep's name, or their email when they have none. */
  label: string;
  /** Shown beside a named rep's link; absent when the label is already the email. */
  email?: string;
}

function toTransferItem(rep: Pick<User, 'oid' | 'name' | 'email'>): TransferItem {
  return rep.name
    ? { key: rep.oid, title: `${rep.name} (${rep.email})`, label: rep.name, email: rep.email }
    : { key: rep.oid, title: rep.email, label: rep.email };
}

export function ConferenceAssignments({ conferenceId }: { conferenceId: string }) {
  const { companyId: scope, companies } = useCompanyScope();
  const queryClient = useQueryClient();

  const [granted, setGranted] = useState<Company[]>([]);
  const [assigned, setAssigned] = useState<UserWithAssignments[]>([]);
  // Full rep list per company id (assigned + unassigned), for the Transfer's left side.
  const [companyReps, setCompanyReps] = useState<Record<string, User[]>>({});
  const [picked, setPicked] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Companies with access, narrowed to the operate-as-tenant scope when set.
  const visibleGranted = scope ? granted.filter((g) => g.id === scope) : granted;

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [companiesWithAccess, users] = await Promise.all([
        listConferenceCompanies(conferenceId),
        listConferenceUsers(conferenceId),
      ]);
      setGranted(companiesWithAccess);
      setAssigned(users);
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  }, [conferenceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // After a successful write, refresh this tab and mark the Overview tab's
  // companies / reps / counts stale (its queries share this key prefix; see
  // views/ConferenceDetail/Overview.tsx). antd keeps a visited tab mounted, so
  // the Overview would otherwise keep showing the pre-write lists.
  const refreshAfterWrite = useCallback(async () => {
    void queryClient.invalidateQueries({ queryKey: ['conferenceOverview', conferenceId] });
    await refresh();
  }, [queryClient, conferenceId, refresh]);

  // Fetch each visible-granted company's full rep list so the Transfer's
  // "Unassigned" side is populated. Re-runs when the grant set or scope changes.
  useEffect(() => {
    let cancelled = false;
    async function loadReps() {
      try {
        const entries = await Promise.all(
          visibleGranted.map(async (company) => {
            const body = await superadminFetch<{ users: User[] }>(
              `/v1/superadmin/users?companyId=${encodeURIComponent(company.id)}`,
            );
            return [company.id, body.users ?? []] as const;
          }),
        );
        if (!cancelled) setCompanyReps(Object.fromEntries(entries));
      } catch (err) {
        if (!cancelled) {
          message.error((err as { message?: string }).message ?? 'Failed to load company reps');
        }
      }
    }
    void loadReps();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [granted, scope, conferenceId]);

  async function grant() {
    if (!picked) return;
    setBusy(true);
    try {
      await grantCompanyAccess(conferenceId, picked);
      message.success('Company granted access');
      setPicked(undefined);
      await refreshAfterWrite();
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Grant failed');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(companyId: string) {
    setBusy(true);
    try {
      await revokeCompanyAccess(conferenceId, companyId);
      message.success('Access revoked');
      await refreshAfterWrite();
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Revoke failed');
    } finally {
      setBusy(false);
    }
  }

  async function onTransfer(
    direction: TransferDirection,
    keys: Key[],
    company: { id: string; name: string },
  ) {
    const oids = keys.map(String);
    if (oids.length === 0) return;
    setBusy(true);
    try {
      if (direction === 'right') {
        await bulkAssign(conferenceId, company.id, oids);
        message.success(`Assigned ${oids.length} to ${company.name}`);
      } else {
        await bulkUnassign(conferenceId, company.id, oids);
        message.success(`Unassigned ${oids.length} from ${company.name}`);
      }
      await refreshAfterWrite();
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Update failed');
    } finally {
      setBusy(false);
    }
  }

  const grantedIds = new Set(granted.map((g) => g.id));
  // Grant picker: ungranted companies, narrowed to the scoped tenant when set.
  const ungranted = companies
    .filter((c) => !grantedIds.has(c.id))
    .filter((c) => (scope ? c.id === scope : true));

  const companyBaseColumns: ColumnsType<Company> = [
    {
      dataIndex: 'name',
      title: 'Name',
      width: 320,
      sorter: (a, b) => a.name.localeCompare(b.name),
      defaultSortOrder: 'ascend',
      render: (_, record: Company) => <CompanyLink id={record.id} name={record.name} />,
    },
    {
      title: 'Actions',
      width: 120,
      render: (_, record: Company) => (
        <Can I="manage" a="Conference" passThrough>
          {(allowed: boolean) =>
            allowed ? (
              <Popconfirm title="Revoke access?" onConfirm={() => revoke(record.id)}>
                <Button danger size="small">
                  Revoke
                </Button>
              </Popconfirm>
            ) : (
              <>—</>
            )
          }
        </Can>
      ),
    },
  ];
  const { columns: companyColumns, components: companyComponents } =
    useResizableColumns<Company>('confGrants', companyBaseColumns);

  if (loading) return <Spin />;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card title="Companies with access" size="small" loading={busy}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Can I="manage" a="Conference" passThrough>
            {(allowed: boolean) =>
              allowed ? (
                <Space wrap>
                  <Select
                    style={{ width: 300, maxWidth: '100%' }}
                    placeholder="Pick a company"
                    value={picked}
                    options={ungranted.map((c) => ({ label: c.name, value: c.id }))}
                    onChange={setPicked}
                    disabled={busy}
                    showSearch
                    filterOption={(input, option) =>
                      ((option?.label as string) ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                  <Button type="primary" onClick={grant} disabled={!picked}>
                    Grant access
                  </Button>
                </Space>
              ) : null
            }
          </Can>
          <Table<Company>
            size="small"
            rowKey="id"
            dataSource={visibleGranted}
            pagination={false}
            columns={companyColumns}
            components={companyComponents}
          />
        </Space>
      </Card>

      <Card title="Users assigned to this conference" size="small" loading={busy}>
        {visibleGranted.length === 0 ? (
          <Typography.Text type="secondary">
            Grant a company access above, then assign its users here.
          </Typography.Text>
        ) : (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {visibleGranted.map((company) => {
              const assignedOids = new Set(
                assigned.filter((u) => u.companyId === company.id).map((u) => u.oid),
              );
              // Union fetched reps with any assigned users for this company
              // (defensive — guarantees assigned reps show on the right side).
              const repById = new Map<string, TransferItem>();
              for (const rep of companyReps[company.id] ?? []) {
                repById.set(rep.oid, toTransferItem(rep));
              }
              for (const u of assigned.filter((a) => a.companyId === company.id)) {
                if (!repById.has(u.oid)) {
                  repById.set(u.oid, toTransferItem(u));
                }
              }
              const dataSource = [...repById.values()];
              const targetKeys = [...assignedOids];

              return (
                <div key={company.id}>
                  <Typography.Text strong>
                    <CompanyLink id={company.id} name={company.name} />
                  </Typography.Text>
                  <div style={{ marginTop: space.sm }}>
                    <Can I="manage" a="Conference" passThrough>
                      {(allowed: boolean) => (
                        <Transfer<TransferItem>
                          dataSource={dataSource}
                          targetKeys={targetKeys}
                          onChange={(_next, direction, moveKeys) =>
                            void onTransfer(direction, moveKeys, company)
                          }
                          showSearch
                          // `{ label, value }`: antd renders `label` and
                          // searches `value`, so the plain "Name (email)"
                          // string keeps driving the Transfer's search.
                          render={(item) => ({
                            label: (
                              <span>
                                <UserLink oid={item.key} name={item.label} />
                                {item.email ? (
                                  <Typography.Text type="secondary"> ({item.email})</Typography.Text>
                                ) : null}
                              </span>
                            ),
                            value: item.title,
                          })}
                          titles={['Unassigned', 'Assigned']}
                          disabled={busy || !allowed}
                          style={{ width: '100%' }}
                          // `minWidth: 0`: antd's own list minimum puts two lists
                          // past a phone's width.
                          listStyle={{ flex: 1, height: 320, minWidth: 0 }}
                        />
                      )}
                    </Can>
                  </div>
                </div>
              );
            })}
          </Space>
        )}
      </Card>
    </Space>
  );
}
